import Alert from "@mui/material/Alert";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useAuditIdentityOptions } from "@/shared/components/principal";
import {
  ResourceView,
  filterToCondition,
  tagFilter,
  useResourceView,
  ViewPicker,
  SaveViewControl,
  useLoadedView,
  useRecordTableState,
  useRestoredTableState,
  useViewFromLink,
  type FiltersHandle,
  type FilterDefinition,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  CustomFieldRemovedColumnsAlert,
  useCustomFieldGridIntegration,
} from "@/shared/components/ResourceView/customFields";
import { TargetEntityType } from "@/shared/domain/customFields";
import { type Device } from "@/shared/domain/devices";
import { devicesTableColumns } from "@/shared/domain/devices/DevicesTableColumns";
import { deviceQueryKeys } from "@/shared/domain/devices/hooks/deviceQueryKeys";
import {
  composeQueryBody,
  QueryContentMode,
  QueryTarget,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useDeviceTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  buildColumnsForPreferences,
  colId,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
} from "@/shared/foundation/primitives/Table";

import styles from "./DevicesList.module.css";

const STATIC_DEVICE_PICKER_OPTIONS: ColumnPickerOption[] =
  devicesTableColumns.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));

interface DevicesListProps {
  selectionOptions?: SelectionOption<Device>[];
  /** Right-aligned toolbar content, e.g. the page's "New Device" button. */
  actions?: React.ReactNode;
}

export function DevicesList({ selectionOptions, actions }: DevicesListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useDeviceTablePreferences();

  // Directories backing the `created_by` identity filter.
  const identityOptions = useAuditIdentityOptions(resolvedOrgId);

  const [initialColumnIds] = React.useState(() => preferences.columns);

  const customFields = useCustomFieldGridIntegration<Device>({
    entityType: TargetEntityType.Device,
    preferenceOrder: initialColumnIds,
    savePreferences,
  });

  const columns = React.useMemo<ColDef<Device>[]>(
    () =>
      buildColumnsForPreferences<Device>({
        staticColumns: devicesTableColumns,
        dynamicColumns: customFields.customFieldColumns,
        preferenceOrder: customFields.effectivePreferenceOrder,
      }),
    [customFields.customFieldColumns, customFields.effectivePreferenceOrder],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_DEVICE_PICKER_OPTIONS,
      ...customFields.columnPickerOptions,
    ],
    [customFields.columnPickerOptions],
  );

  // A `?view=` link is resolved in the route loader, so the View it names
  // is available here — before the table's first query is composed. Seeding
  // it is what keeps a shared link from rendering an unfiltered page first.
  const linked = useViewFromLink<Device>({
    getRowId: (row) => row.device_id,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });
  const restored = useRestoredTableState({
    target: QueryTarget.Devices,
    link: linked,
  });
  const view = useResourceView<Device>(restored.options);

  // Which saved View is applied, and the routes in and out of one.
  const viewSession = useLoadedView(view, restored.view);
  useRecordTableState({
    target: QueryTarget.Devices,
    view,
    loadedView: viewSession.loadedView,
  });
  // Lets the View picker's empty state open the filter menu on a field.
  const filtersRef = React.useRef<FiltersHandle>(null);

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      { type: "string", field: "device_id", name: "Device ID" },
      // The Devices catalog accepts only membership comparators on `tags`;
      // an unrestricted string row would offer equality/substring filters
      // the backend rejects on submit.
      { type: "string", field: "tags", name: "Tags", ...tagFilter },
      {
        type: "identity",
        field: "created_by",
        name: "Created By",
        options: identityOptions,
      },
      { type: "date", field: "created", name: "Created" },
      { type: "date", field: "modified", name: "Modified" },
      ...customFields.filterDefinitions,
    ],
    [customFields.filterDefinitions, identityOptions],
  );

  const filterCondition =
    view.filters.type === "structured" && view.filters.filters.length > 0
      ? filterToCondition(view.filters.filters, {
          matchMode: view.filters.matchMode,
        })
      : undefined;

  const query = composeQueryBody({
    scopeCondition: undefined,
    filterCondition,
    sort: view.table.sort,
  });

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Devices,
    contentMode: QueryContentMode.RecordWithMeta,
    queryKey: deviceQueryKeys.list(resolvedOrgId, query),
  });
  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<Device>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  const loading = deriveLoadingState({
    isLoading: submitQuery.isLoading || pageQuery.isLoading,
    isFetching: submitQuery.isFetching || pageQuery.isFetching,
  });
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <CustomFieldRemovedColumnsAlert {...customFields.removedColumnsAlert} />
      <ResourceView.Root>
        <ResourceView.Actions>
          <ViewPicker
            target={QueryTarget.Devices}
            loadedView={viewSession.loadedView}
            onSelectView={viewSession.selectView}
            seededViewId={viewSession.seededViewId}
            arrivalClaimed={restored.arrivalClaimed}
            onClearView={viewSession.clearView}
            linkFor={viewSession.linkFor}
            filterDefinitions={filterDefinitions}
            onStartFilter={(field) => filtersRef.current?.openWithField(field)}
          />
          <ResourceView.Filters
            ref={filtersRef}
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          <ResourceView.Selection<Device>
            selection={view.table.selection}
            clearSelection={view.table.clearSelection}
            options={selectionOptions ?? []}
          />
          {actions && <div className={styles.actionsRight}>{actions}</div>}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          trailingActions={
            <SaveViewControl
              view={view}
              target={QueryTarget.Devices}
              session={viewSession}
            />
          }
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<Device>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No devices"
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
        />
      </ResourceView.Root>
    </>
  );
}
